package com.anjuros.website.redesign.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.components.language.ResKey
import com.anjuros.website.components.language.UiMode
import com.anjuros.website.redesign.styles.RdArrowStyle
import com.anjuros.website.redesign.styles.TtBackLabelStyle
import com.anjuros.website.redesign.styles.TtBackStyle
import com.anjuros.website.redesign.styles.TtBarStyle
import com.anjuros.website.redesign.styles.TtCardButtonStyle
import com.anjuros.website.redesign.styles.TtCardHeadStyle
import com.anjuros.website.redesign.styles.TtCloseButtonStyle
import com.anjuros.website.redesign.styles.TtDescriptionStyle
import com.anjuros.website.redesign.styles.TtDividerStyle
import com.anjuros.website.redesign.styles.TtEyebrowStyle
import com.anjuros.website.redesign.styles.TtHintButtonStyle
import com.anjuros.website.redesign.styles.TtHintStyle
import com.anjuros.website.redesign.styles.TtHintTitleStyle
import com.anjuros.website.redesign.styles.TtIconButtonStyle
import com.anjuros.website.redesign.styles.TtLogoStyle
import com.anjuros.website.redesign.styles.TtLogoTopStyle
import com.anjuros.website.redesign.styles.TtLogoBottomStyle
import com.anjuros.website.redesign.styles.TtMoreLinksStyle
import com.anjuros.website.redesign.styles.TtSheetLinksStyle
import com.anjuros.website.redesign.styles.TtModeButtonBarActiveStyle
import com.anjuros.website.redesign.styles.TtModeButtonBarStyle
import com.anjuros.website.redesign.styles.TtModeButtonSheetActiveStyle
import com.anjuros.website.redesign.styles.TtModeButtonSheetStyle
import com.anjuros.website.redesign.styles.TtModeGroupBarStyle
import com.anjuros.website.redesign.styles.TtModeGroupSheetStyle
import com.anjuros.website.redesign.styles.TtModeGroupSpotlightStyle
import com.anjuros.website.redesign.styles.TtPillLabelStyle
import com.anjuros.website.redesign.styles.TtPillStyle
import com.anjuros.website.redesign.styles.TtPopoverOpenStyle
import com.anjuros.website.redesign.styles.TtPopoverStyle
import com.anjuros.website.redesign.styles.TtSheetButtonStyle
import com.anjuros.website.redesign.styles.TtSheetDescriptionStyle
import com.anjuros.website.redesign.styles.TtSheetHandleStyle
import com.anjuros.website.redesign.styles.TtSheetScrimOpenStyle
import com.anjuros.website.redesign.styles.TtSheetScrimStyle
import com.anjuros.website.redesign.styles.TtSheetStyle
import com.anjuros.website.redesign.styles.TtSheetTitleStyle
import com.anjuros.website.redesign.styles.TtStackStyle
import com.anjuros.website.redesign.styles.TtTitleStyle
import com.anjuros.website.redesign.styles.TtTitleWrapStyle
import com.anjuros.website.redesign.util.RdIcons
import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement


/**
 * The bar above every template preview: back to all templates, the template name, "About this design"
 * and the Example content / Your details switch. On phones the switch lives in the About sheet.
 *
 * [showHint] is the first-visit hint: on wider screens it spotlights the switch, on phones it opens the sheet.
 */
@Composable
fun TemplateToolbar(
    modifier: Modifier = Modifier,
    title: ResKey?,
    description: ResKey?,
    contentModeSelected: Boolean?,
    onChangeMode: (UiMode) -> Unit,
    showHint: Boolean = false,
    onDismissHint: () -> Unit = {},
) {
    val isPhone = rememberBreakpoint() < Breakpoint.MD
    var aboutOpen by remember { mutableStateOf(false) }
    val backLabel = LocalText(RdText.TOOLBAR_BACK_LABEL)
    val homeLabel = LocalText(RdText.TOOLBAR_HOME_LABEL)
    val aboutLabel = LocalText(RdText.TOOLBAR_ABOUT_DESIGN)
    val previewLabel = LocalText(RdText.TOOLBAR_PREVIEW_LABEL)

    LaunchedEffect(showHint, isPhone) {
        if (showHint && isPhone) aboutOpen = true
    }

    // While the phone sheet is open the page behind it must not scroll.
    val sheetShown = aboutOpen && isPhone
    DisposableEffect(sheetShown) {
        val root = document.documentElement as? HTMLElement
        val body = document.body
        if (sheetShown) {
            root?.style?.setProperty("overflow", "hidden")
            body?.style?.setProperty("overflow", "hidden")
        }
        onDispose {
            root?.style?.removeProperty("overflow")
            body?.style?.removeProperty("overflow")
        }
    }

    fun closeAbout() {
        aboutOpen = false
        if (showHint) onDismissHint()
    }

    Header(TtBarStyle.toModifier().then(modifier).toAttrs()) {
        RdLink(RdRoute.HOME, TtLogoStyle.toModifier().attrsModifier { attr("aria-label", homeLabel) }) {
            Span(TtLogoTopStyle.toModifier().toAttrs()) { Text(LocalText(RdText.TOOLBAR_LOGO_TOP)) }
            Span(TtLogoBottomStyle.toModifier().toAttrs()) { Text(LocalText(RdText.TOOLBAR_LOGO_BOTTOM)) }
        }
        Div(TtDividerStyle.toModifier().toAttrs())
        RdLink(
            RdRoute.DESIGNS_CATALOGUE,
            TtBackStyle.toModifier().attrsModifier { attr("aria-label", backLabel) }
        ) {
            RdIcons.ChevronLeft()
            Span(TtBackLabelStyle.toModifier().toAttrs()) { Text(LocalText(RdText.TOOLBAR_ALL_TEMPLATES)) }
        }
        Div(TtDividerStyle.toModifier().toAttrs())

        Div(TtTitleWrapStyle.toModifier().toAttrs()) {
            H1(TtTitleStyle.toModifier().toAttrs()) { Text(LocalText(title)) }
            Button(
                TtIconButtonStyle.toModifier()
                    .attrsModifier {
                        attr("type", "button")
                        attr("aria-label", aboutLabel)
                        attr("aria-expanded", aboutOpen.toString())
                        attr("aria-controls", "template-about")
                    }
                    .onClick { if (aboutOpen) closeAbout() else aboutOpen = true }
                    .toAttrs()
            ) { RdIcons.Info() }

            AboutPopover(open = aboutOpen, description = description, onClose = ::closeAbout)
        }

        Div(
            TtModeGroupBarStyle.toModifier()
                .thenIf(showHint, TtModeGroupSpotlightStyle.toModifier())
                .attrsModifier { attr("role", "group"); attr("aria-label", previewLabel) }
                .toAttrs()
        ) {
            ModeButtons(contentModeSelected, onChangeMode, TtModeButtonBarStyle.toModifier(), TtModeButtonBarActiveStyle.toModifier())
            if (showHint && !isPhone) ModeHint(onDismissHint)
        }

        AboutSheet(
            open = aboutOpen,
            title = title,
            description = description,
            contentModeSelected = contentModeSelected,
            onChangeMode = onChangeMode,
            onClose = ::closeAbout
        )
    }
}

/** The floating "Like this design? Get this website" button shown on every template preview. */
@Composable
fun TemplateGetWebsitePill() {
    RdLink(RdRoute.CONTACT, TtPillStyle.toModifier()) {
        Span(TtPillLabelStyle.toModifier().toAttrs()) { Text(LocalText(RdText.TOOLBAR_PILL)) }
        Span(RdArrowStyle.toModifier().attrsModifier { attr("aria-hidden", "true") }.toAttrs()) { Text("→") }
    }
}

@Composable
private fun ModeButtons(
    contentModeSelected: Boolean?,
    onChangeMode: (UiMode) -> Unit,
    style: Modifier,
    activeStyle: Modifier,
) {
    // No saved mode yet (null) means the page shows the example content.
    ModeButton(contentModeSelected != false, RdText.TOOLBAR_EXAMPLE_CONTENT, style, activeStyle, { RdIcons.ExampleContent() }) {
        onChangeMode(UiMode.NORMAL)
    }
    ModeButton(contentModeSelected == false, RdText.TOOLBAR_YOUR_DETAILS, style, activeStyle, { RdIcons.YourDetails() }) {
        onChangeMode(UiMode.PLACEHOLDER)
    }
}

@Composable
private fun ModeButton(
    active: Boolean,
    text: RdText,
    style: Modifier,
    activeStyle: Modifier,
    icon: @Composable () -> Unit,
    onClick: () -> Unit,
) {
    Button(
        style
            .thenIf(active, activeStyle)
            .attrsModifier { attr("type", "button"); attr("aria-pressed", active.toString()) }
            .onClick { onClick() }
            .toAttrs()
    ) {
        icon()
        Text(LocalText(text))
    }
}

/** Wider screens: the description under the info button. Always in the page (hidden until opened) so it is indexed. */
@Composable
private fun AboutPopover(open: Boolean, description: ResKey?, onClose: () -> Unit) {
    val aboutLabel = LocalText(RdText.TOOLBAR_ABOUT_DESIGN)
    Div(
        TtPopoverStyle.toModifier()
            .thenIf(open, TtPopoverOpenStyle.toModifier())
            .id("template-about")
            .attrsModifier { attr("role", "dialog"); attr("aria-label", aboutLabel) }
            .toAttrs()
    ) {
        Div(TtCardHeadStyle.toModifier().toAttrs()) {
            Span(TtEyebrowStyle.toModifier().toAttrs()) { Text(LocalText(RdText.TOOLBAR_ABOUT_DESIGN)) }
            CloseButton(onClose)
        }
        if (description != null) {
            H2(TtDescriptionStyle.toModifier().toAttrs()) { Text(LocalText(description)) }
        }
        RdLink(RdRoute.CONTACT, TtCardButtonStyle.toModifier()) { Text(LocalText(RdText.TOOLBAR_GET_WEBSITE)) }
        SiteLinks(TtMoreLinksStyle.toModifier())
    }
}

/** Phones: a bottom sheet with the description, the preview switch and the call to action. */
@Composable
private fun AboutSheet(
    open: Boolean,
    title: ResKey?,
    description: ResKey?,
    contentModeSelected: Boolean?,
    onChangeMode: (UiMode) -> Unit,
    onClose: () -> Unit,
) {
    val aboutLabel = LocalText(RdText.TOOLBAR_ABOUT_DESIGN)
    val previewLabel = LocalText(RdText.TOOLBAR_PREVIEW_LABEL)
    Div(
        TtSheetScrimStyle.toModifier()
            .thenIf(open, TtSheetScrimOpenStyle.toModifier())
            .onClick { onClose() }
            .toAttrs()
    ) {
        Div(
            TtSheetStyle.toModifier()
                .attrsModifier { attr("role", "dialog"); attr("aria-label", aboutLabel) }
                .onClick { it.stopPropagation() }
                .toAttrs()
        ) {
            Div(TtSheetHandleStyle.toModifier().toAttrs())
            Div(TtCardHeadStyle.toModifier().toAttrs()) {
                Div {
                    Span(TtEyebrowStyle.toModifier().toAttrs()) { Text(LocalText(RdText.TOOLBAR_ABOUT_DESIGN)) }
                    Span(TtSheetTitleStyle.toModifier().toAttrs()) { Text(LocalText(title)) }
                }
                CloseButton(onClose)
            }
            if (description != null) {
                P(TtSheetDescriptionStyle.toModifier().toAttrs()) { Text(LocalText(description)) }
            }
            Div(TtStackStyle.toModifier().toAttrs()) {
                Span(TtEyebrowStyle.toModifier().toAttrs()) { Text(LocalText(RdText.TOOLBAR_PREVIEW_WITH)) }
                Div(
                    TtModeGroupSheetStyle.toModifier()
                        .attrsModifier { attr("role", "group"); attr("aria-label", previewLabel) }
                        .toAttrs()
                ) {
                    ModeButtons(contentModeSelected, onChangeMode, TtModeButtonSheetStyle.toModifier(), TtModeButtonSheetActiveStyle.toModifier())
                }
            }
            RdLink(RdRoute.CONTACT, TtCardButtonStyle.toModifier().then(TtSheetButtonStyle.toModifier())) {
                Text(LocalText(RdText.TOOLBAR_GET_WEBSITE))
            }
            SiteLinks(TtSheetLinksStyle.toModifier())
        }
    }
}

@Composable
private fun CloseButton(onClose: () -> Unit) {
    val closeLabel = LocalText(RdText.NAV_CLOSE)
    Button(
        TtCloseButtonStyle.toModifier()
            .attrsModifier { attr("type", "button"); attr("aria-label", closeLabel) }
            .onClick { onClose() }
            .toAttrs()
    ) { RdIcons.Close(size = 20) }
}

/** First-visit hint under the switch (wider screens). */
@Composable
private fun ModeHint(onDismiss: () -> Unit) {
    Div(TtHintStyle.toModifier().attrsModifier { attr("role", "dialog") }.toAttrs()) {
        P(TtHintTitleStyle.toModifier().toAttrs()) { Text(LocalText(RdText.TOOLBAR_HINT_TITLE)) }
        Span { RdRichText(LocalText(RdText.TOOLBAR_HINT_TEXT)) }
        Button(
            TtCardButtonStyle.toModifier()
                .then(TtHintButtonStyle.toModifier())
                .attrsModifier { attr("type", "button") }
                .onClick { onDismiss() }
                .toAttrs()
        ) { Text(LocalText(RdText.TOOLBAR_HINT_BUTTON)) }
    }
}


/** Links back into the main site, for visitors who land on a template straight from Google. */
private val SiteLinkItems = listOf(
    RdText.TOOLBAR_LINK_HOME to RdRoute.HOME,
    RdText.NAV_DESIGNS to RdRoute.DESIGNS,
    RdText.NAV_PRICING to RdRoute.PRICING,
)

@Composable
private fun SiteLinks(modifier: Modifier) {
    val navLabel = LocalText(RdText.TOOLBAR_MORE_LABEL)
    Nav(modifier.attrsModifier { attr("aria-label", navLabel) }.toAttrs()) {
        SiteLinkItems.forEach { (text, path) -> RdLink(path) { Text(LocalText(text)) } }
    }
}
